import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { PanelHeader } from "./components/PanelHeader";
import { PanelContent } from "./components/PanelContent";
import { Dialog, DialogContent } from "@/components/ui/dialog";

import type { BillingOrgRef } from "@/hooks/billing/useSubscription";
import { invalidateBilling } from "@/lib/billing-invalidation";
import { subscriptionKeys } from "@/lib/queryKeys";

export function SubscriptionPanel({
  isOpen,
  onClose,
  org,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Org to show and change; omitted = the user's personal org */
  org?: BillingOrgRef;
}) {
  // The old `refreshSubscription` action ran on every mount to
  // guarantee the panel reflects the latest server state — e.g. the
  // user just completed a checkout in another tab and re-opened the
  // panel. Invalidating the cached subscription key has the same
  // effect: any mounted `useSubscription` consumer (PanelHeader,
  // PanelContent, CurrentPlan) refetches in a single batch.
  const queryClient = useQueryClient();

  useEffect(() => {
    void invalidateBilling(
      queryClient,
      subscriptionKeys.detailPrefix(org?.id ?? "current"),
    );
  }, [queryClient, org?.id]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px] gap-0 p-0">
        <PanelHeader orgId={org?.id} />
        <PanelContent org={org} />
      </DialogContent>
    </Dialog>
  );
}
